import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Prohibit, ArrowClockwise, MagnifyingGlass, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/riq";
import { shortDate } from "@/lib/format";

const SOURCE = { ses_suppression_list: "SES list", ses_webhook: "SES bounce", resend_webhook: "Resend bounce", test: "Test" };

function Row({ r, onUnblock }) {
  return (
    <div data-testid={`suppression-row-${r.email}`} className="flex items-center gap-3 px-4 py-2.5 text-sm border-t border-riq-border/60">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-neutral-800">{r.email}</div>
        <div className="truncate text-xs text-muted-foreground">
          {r.reason === "complaint" ? "Spam complaint" : "Bounced"} · {SOURCE[r.source] || r.source} · {shortDate(r.updated_at)}
          {r.has_account ? (r.account_deactivated ? " · account deactivated" : " · has account") : " · no account"}
          {r.detail ? ` · ${r.detail}` : ""}
        </div>
      </div>
      <button data-testid={`unblock-${r.email}`} onClick={() => onUnblock(r.email)}
        className="shrink-0 rounded-full border border-riq-border px-3 py-1 text-xs font-medium hover:border-riq-primary hover:text-riq-primary transition-colors">
        Unblock
      </button>
    </div>
  );
}

export const EmailSuppressions = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/admin/email-suppressions").then((r) => setData(r.data)).catch(() => setData({ rows: [], count: 0 }));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: d } = await api.post("/admin/email-suppressions/sync");
      toast.success(`Checked ${d.checked} SES addresses`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(false); }
  };
  const unblock = async (email) => {
    if (!window.confirm(`Allow emails to ${email} again and re-activate the account?`)) return;
    try { await api.delete(`/admin/email-suppressions/${encodeURIComponent(email)}`); toast.success("Unblocked"); load(); }
    catch { toast.error("Could not unblock"); }
  };

  const rows = (data?.rows || []).filter((r) => !q || r.email.includes(q.toLowerCase()));
  return (
    <Card data-testid="email-suppressions-card" className="!p-0 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-5">
        <Prohibit size={20} weight="duotone" className="text-riq-primary" />
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-head font-semibold text-riq-primary">Do-not-email list</h3>
          <p className="text-xs text-muted-foreground">Bounced or complaining addresses get no emails and their accounts are deactivated. Synced from SES daily; new bounces arrive instantly once webhooks are set up.</p>
        </div>
        <span data-testid="suppression-count" className="font-head text-2xl font-bold text-riq-primary">{data ? data.count : "…"}</span>
        <button data-testid="suppression-sync-btn" onClick={sync} disabled={syncing}
          className="flex items-center gap-2 rounded-full bg-riq-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
          {syncing ? <CircleNotch size={16} className="animate-spin" /> : <ArrowClockwise size={16} />} Sync from SES
        </button>
      </div>
      <div className="px-5 pb-3">
        <label className="flex items-center gap-2 rounded-xl bg-secondary/60 px-3 py-2">
          <MagnifyingGlass size={16} className="text-muted-foreground" />
          <input data-testid="suppression-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search address"
            className="w-full bg-transparent text-sm outline-none" />
        </label>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {rows.slice(0, 300).map((r) => <Row key={r.email} r={r} onUnblock={unblock} />)}
        {data && rows.length === 0 && <div className="px-5 py-6 text-sm text-muted-foreground">No addresses.</div>}
      </div>
    </Card>
  );
};
